import _ from "lodash";
import {
  mergeSwitchItems,
  percentageToQuantity,
  transactionValueToEuro,
  transactionValueToPercentage,
  transactionValueToQuantity,
} from "../../../utils";
import {QTY_DECIMALS} from "../../../../../utils/utils";


/**
 * Rounds a quantity value to the portfolio's configured decimal precision.
 */
const roundQty = (value, maxQtyDecimals) => _.floor(value, maxQtyDecimals || QTY_DECIMALS);

/**
 * Computes transaction value fields from a quantity-based input.
 *
 * @param {number} value - The raw quantity value
 * @param {Object} item - The transaction instrument
 * @param {Object|undefined} switchOutItem - The switch-out item (for merged calculations)
 * @param {boolean} withEuro - Whether to include euro calculation
 * @param {number|undefined} maxQtyDecimals - Portfolio's max quantity decimal places
 * @returns {{ transaction_value: number, calculated: Object }}
 */
export const computeTransactionValue = (value, item, switchOutItem, withEuro, maxQtyDecimals) => {
  const transactionValue = roundQty(value, maxQtyDecimals);
  const merged = (switchOutItem && mergeSwitchItems(item, switchOutItem)) || item;
  const transactionValueEuro = transactionValueToEuro(transactionValue, merged);
  const transactionValuePercentage = transactionValueToPercentage(transactionValue, merged);

  const calculated = {
    ..._.get(item, 'calculated', {}),
    transaction_value_percentage: transactionValuePercentage,
  };
  if (withEuro) {
    calculated.transaction_value_euro = transactionValueEuro;
  }

  return {
    transaction_value: value,
    calculated,
  };
};

/**
 * Computes transaction value fields from a euro-based input (for switches).
 * Converts euro → quantity, then derives percentage.
 *
 * @param {number} value - The euro amount
 * @param {Object} item - The transaction instrument
 * @param {Object|undefined} switchOutItem - The switch-out item (for merged calculations)
 * @param {number|undefined} maxQtyDecimals - Portfolio's max quantity decimal places
 * @returns {{ transaction_value: number, calculated: Object }}
 */
export const computeSwitchAmountEuro = (value, item, switchOutItem, maxQtyDecimals) => {
  const transactionValueEuro = _.round(value, 2);
  const merged = (switchOutItem && mergeSwitchItems(item, switchOutItem)) || item;
  let transactionValue = transactionValueToQuantity(transactionValueEuro, merged, false);
  transactionValue = transactionValue && roundQty(transactionValue, maxQtyDecimals);
  const transactionValuePercentage = transactionValueToPercentage(transactionValue, merged);

  return {
    transaction_value: transactionValue,
    calculated: {
      ..._.get(item, 'calculated', {}),
      transaction_value_euro: transactionValueEuro,
      transaction_value_percentage: transactionValuePercentage,
    },
  };
};

/**
 * Computes transaction value fields from a percentage-based input (for switches).
 * Converts percentage → quantity, then derives euro.
 *
 * @param {number} value - The percentage value
 * @param {Object} item - The transaction instrument
 * @param {Object|undefined} switchOutItem - The switch-out item (for merged calculations)
 * @param {number|undefined} maxQtyDecimals - Portfolio's max quantity decimal places
 * @returns {{ transaction_value: number, calculated: Object }}
 */
export const computeSwitchPercentage = (value, item, switchOutItem, maxQtyDecimals) => {
  const transactionValuePercentage = _.round(value, 2);
  const merged = (switchOutItem && mergeSwitchItems(item, switchOutItem)) || item;
  let transactionValue = percentageToQuantity(value, merged, false);
  transactionValue = transactionValue && roundQty(transactionValue, maxQtyDecimals);
  const transactionValueEuro = transactionValueToEuro(transactionValue, merged);

  return {
    transaction_value: transactionValue,
    calculated: {
      ..._.get(item, 'calculated', {}),
      transaction_value_percentage: transactionValuePercentage,
      transaction_value_euro: transactionValueEuro,
    },
  };
};
